import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type { ClientOption, PickedImage } from "./AddGlobalProductView";

/**
 * SCRUM-2263: the Add Global Product form, written as a createSlice like every
 * reducer in this client (SCRUM-1954) and mounted LOCALLY with
 * `useReducer(productForm.reducer, ...)` — the unsaved draft of the form, not
 * store data. The SCRUM-2127 pattern. The update screen joins it in SCRUM-2264
 * with `product`, `alt_pic` and the three product* actions; it does not use
 * `productImg` or `selectedClient` (it reads both off `product`).
 */
type ProductFormState = {
  title: string;
  productImg: PickedImage;
  sponsored: boolean;
  // A number once parsed; the raw string while the field is empty or not numeric.
  rating: number | string;
  selectedClient: ClientOption;
  /** Update screen: the loaded product as edited so far, client as an id. Null on the add screen. */
  product: Record<string, any> | null; // any: product_byId is typed any (productSlice.ts:48); no client Product type yet
  alt_pic: string;
};

/** The form fields that move together with the update screen's working copy. */
type ProductEdit = { title?: string; alt_pic?: string; sponsored?: boolean; rating?: number; [key: string]: unknown };

const NO_CLIENT: ClientOption = { label: "", value: "" };

const initialState: ProductFormState = {
  title: "",
  productImg: {},
  sponsored: false,
  rating: 0,
  selectedClient: NO_CLIENT,
  product: null,
  alt_pic: "",
};

export const productForm = createSlice({
  name: "productForm",
  initialState,
  reducers: {
    titleChanged(state, action: PayloadAction<string>) {
      state.title = action.payload;
    },
    // The raw field value: a number is clamped to 0..10, anything else (empty,
    // not numeric) is kept as typed so the field stays editable.
    ratingChanged(state, action: PayloadAction<string>) {
      const raw = action.payload;
      if (raw !== "" && !isNaN(Number(raw))) {
        state.rating = Math.min(10, Math.max(0, parseFloat(raw)));
      } else {
        state.rating = raw;
      }
    },
    sponsoredToggled(state, action: PayloadAction<boolean>) {
      state.sponsored = action.payload;
    },
    clientSelected(state, action: PayloadAction<ClientOption | null>) {
      state.selectedClient = action.payload ?? NO_CLIENT;
    },
    imageReceived(state, action: PayloadAction<PickedImage | null>) {
      state.productImg = action.payload || {};
    },
    // After a successful add the text fields start over; the picked image is
    // not cleared here (it never was).
    saved(state) {
      state.title = "";
      state.sponsored = false;
      state.rating = 0;
      state.selectedClient = NO_CLIENT;
    },
    // Update screen: the product read arrived. First load wins: once a product is
    // loaded, a later store refresh (after a save) does not overwrite the edits.
    productLoaded(state, action: PayloadAction<Record<string, any>>) { // any: product_byId is typed any (productSlice.ts:48)
      if (state.product?._id) return;
      const p = action.payload;
      state.product = { ...p, client: p.client?._id || null };
      state.title = p.title || "";
      state.alt_pic = p.alt_pic || "";
      state.sponsored = !!p.sponsored;
      state.rating = typeof p.rating === "number" ? p.rating : 0;
    },
    // Update screen: an edit lands on the working copy, and the form field it names moves with it.
    productEdited(state, action: PayloadAction<ProductEdit>) {
      const edit = action.payload;
      state.product = { ...state.product, ...edit };
      if (edit.title !== undefined) state.title = edit.title;
      if (edit.alt_pic !== undefined) state.alt_pic = edit.alt_pic;
      if (edit.sponsored !== undefined) state.sponsored = edit.sponsored;
      if (edit.rating !== undefined) state.rating = edit.rating;
    },
    // Update screen: the link toggle's optimistic copy, or its revert after a failure.
    productReplaced(state, action: PayloadAction<Record<string, any>>) { // any: as productLoaded
      state.product = action.payload;
    },
  },
});

/**
 * The client select's options, the "None" row included. The add screen lists
 * "None" last, the update screen first; with no clients both show only "None".
 */
export const clientOptions = (
  clients: { _id: string; name: string }[] | null | undefined,
  nonePosition: "first" | "last"
): ClientOption[] => {
  const none: ClientOption = { value: "", label: "None" };
  const options = (clients || []).map((cl) => ({ value: cl._id, label: cl.name }));
  return nonePosition === "first" ? [none, ...options] : [...options, none];
};
